/**
 * 知识库上传
 * @author jayceecai
 */
import React, { useCallback, useEffect, useState } from 'react'
import { createForm, Form } from '@library'
import { Modal, Input, message, Radio } from 'antd'
import type { UploadFile } from 'antd'
import { KnowledgeUploadParam } from '@types'
import type { CheckboxGroupProps } from 'antd/es/checkbox'
import CosUpload from '../../../components/COSUpload'

const { TextArea } = Input

// 表单状态管理
const formStore = createForm({
  type: 0, // 文档类型
  tag: 0, // 文档标签
  title: '', // 文档标题
  remark: '', // 文档描述
  // file: undefined,
  fileURL: '', // 文件URL
  fileType: '', // 文件类型
} as Partial<KnowledgeUploadParam>)

const typeoptions: CheckboxGroupProps['options'] = [
  { label: '话术模版', value: 1 },
  { label: '活动信息', value: 2 },
]
const tagoptions: CheckboxGroupProps['options'] = [
  { label: '行业话术', value: 1 },
  { label: '产品话术', value: 2 },
  { label: '活动话术', value: 3 },
  { label: '通用话术', value: 4 },
]

const { useValues, useValidate, Field, withForm } = formStore

interface KnowledgeModalProps {
  visible: boolean
  onCancel: () => void
  onSubmit?: (formData: KnowledgeUploadParam) => Promise<void>
  pathPrefix?: string
  children?: React.ReactNode
}

const KnowledgeModal: React.FC<KnowledgeModalProps> = ({ visible, onCancel, onSubmit, pathPrefix, children }) => {
  const { validate } = useValidate()
  const { values, setValues } = useValues()
  const [confirmLoading, setConfirmLoading] = useState(false)

  const handleFilesChange = async (keys: string[], files: UploadFile[]) => {
    const filename = files[0]?.name || ''
    const path = filename ? (pathPrefix ? `${pathPrefix}/${filename}` : filename) : ''
    const fileExtension = filename?.split('.').pop() || ''
    setValues({
      ...values,
      fileURL: path,
      fileType: fileExtension,
    })
  }

  // 提交
  const handleSubmit = useCallback(async () => {
    setConfirmLoading(true)
    const validateRes = await validate()
    if (!validateRes.isPass) {
      setConfirmLoading(false)
      return
    }
    if (values.type === 1 && (!values.fileURL || !values.fileType)) {
      message.warning('请上传文件')
      setConfirmLoading(false)
      return
    }
    if (onSubmit) {
      try {
        await onSubmit(values as KnowledgeUploadParam)
      } finally {
        setConfirmLoading(false)
      }
    } else {
      setConfirmLoading(false)
    }
  }, [onSubmit, values, validate])

  return (
    <Modal
      title="知识库上传"
      width={800}
      open={visible}
      okText="确定"
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
    >
      <Form labelCol={{ span: 4 }} wrapperCol={{ span: 18 }}>
        <Field required field="type" label="知识类型">
          <Radio.Group options={typeoptions} />
        </Field>
        {values.type === 1 && (
          <Field required field="tag" label="知识标签">
            <Radio.Group options={tagoptions} />
          </Field>
        )}
        <Field required field="title" label="知识标题">
          <Input placeholder="请输入知识标题" />
        </Field>
        <Field required field="remark" label="知识描述">
          <TextArea
            placeholder="请输入知识描述"
            rows={4}
            maxLength={1000}
            showCount
          />
        </Field>
        <Field required={values.type === 1} field="fileURL" label="文件上传">
          <div>
            <CosUpload
              size={10}
              multiple={false}
              maxCount={1}
              accept=".iwiki,.pdf,.csv,.docx,.xlsx,.pptx,.jpeg,.png,.jpg"
              pathPrefix={pathPrefix}
              isShowUploadTemplate={false}
              onChange={handleFilesChange}
            />
          </div>
        </Field>
      </Form>
    </Modal>
  )
}

export default withForm(KnowledgeModal)
